import { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "wouter";
import {
  LogOut, FileText, Phone, Mail, Clock, User, Plus, ArrowRight, CheckCircle2, Loader2, AlertCircle,
  LayoutDashboard, ClipboardList, Settings, Lock, Eye, EyeOff, Check, X,
  Lightbulb, Flame, Plug, Zap, Snowflake, Wind, Building2, FolderOpen, Download, FileCheck2, AlertTriangle, Paperclip,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell,
} from "recharts";
import logo from "/images/brand/vertex-logo.png";

type PortalKind = "customer" | "company";

interface PortalUser {
  kind: PortalKind;
  id: number;
  email: string;
  name: string;
  phone?: string | null;
  address?: string | null;
  operatingCompany?: string | null;
  website?: string | null;
  contactName?: string | null;
  accreditations?: string[];
  createdAt: string;
}

interface Submission {
  id: number;
  formType: string;
  name: string;
  email: string | null;
  phone: string | null;
  subject: string | null;
  message: string | null;
  status: string | null;
  createdAt: string;
}

interface PpmRow {
  id: number;
  ownerType: string;
  ownerId: number;
  serviceType: string;
  lastServiced: string | null;
  dueDate: string | null;
  notes: string | null;
}

const STATUS_META: Record<string, { label: string; color: string; icon: typeof CheckCircle2 }> = {
  new: { label: "New", color: "bg-blue-100 text-blue-700", icon: AlertCircle },
  in_progress: { label: "In Progress", color: "bg-amber-100 text-amber-700", icon: Loader2 },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700", icon: AlertCircle },
};

const PPM_SERVICES = [
  { key: "emergency_lighting", label: "Emergency Lighting", icon: Lightbulb, color: "#f59e0b" },
  { key: "fire_alarm", label: "Fire Alarm", icon: Flame, color: "#dc2626" },
  { key: "pat_testing", label: "PAT Testing", icon: Plug, color: "#2563eb" },
  { key: "eicr", label: "EICR", icon: Zap, color: "#7c3aed" },
  { key: "refrigeration", label: "Refrigeration", icon: Snowflake, color: "#06b6d4" },
  { key: "hvac", label: "HVAC", icon: Wind, color: "#10b981" },
];

type TabKey = "overview" | "requests" | "ppm" | "callouts" | "account";

function ppmStatus(row?: PpmRow): { label: "Overdue" | "Due" | "OK" | "Missing"; color: string; bg: string; days: number | null } {
  if (!row || (!row.dueDate && !row.lastServiced)) return { label: "Missing", color: "text-slate-500", bg: "bg-slate-100", days: null };
  if (!row.dueDate) return { label: "OK", color: "text-emerald-700", bg: "bg-emerald-100", days: null };
  const due = new Date(row.dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { label: "Overdue", color: "text-red-700", bg: "bg-red-100", days: diffDays };
  if (diffDays <= 30) return { label: "Due", color: "text-amber-700", bg: "bg-amber-100", days: diffDays };
  return { label: "OK", color: "text-emerald-700", bg: "bg-emerald-100", days: diffDays };
}

export default function Portal() {
  const [, setLocation] = useLocation();
  const [user, setUser] = useState<PortalUser | null>(null);
  const [subs, setSubs] = useState<Submission[]>([]);
  const [ppm, setPpm] = useState<PpmRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>("overview");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const meRes = await fetch(`${import.meta.env.BASE_URL}api/customer/me`, { credentials: "include" });
        if (!meRes.ok) { setLocation("/login"); return; }
        const me: PortalUser = await meRes.json();
        if (cancelled) return;
        setUser(me);
        const [subsRes, ppmRes] = await Promise.all([
          fetch(`${import.meta.env.BASE_URL}api/customer/submissions`, { credentials: "include" }),
          fetch(`${import.meta.env.BASE_URL}api/customer/ppm`, { credentials: "include" }),
        ]);
        if (subsRes.ok && !cancelled) setSubs(await subsRes.json());
        if (ppmRes.ok && !cancelled) setPpm(await ppmRes.json());
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [setLocation]);

  const handleLogout = async () => {
    await fetch(`${import.meta.env.BASE_URL}api/customer/logout`, { method: "POST", credentials: "include" });
    setLocation("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }
  if (!user) return null;

  const isCompany = user.kind === "company";
  const firstName = isCompany ? user.name : user.name.split(" ")[0];

  const ppmByService = new Map<string, PpmRow>(ppm.map(p => [p.serviceType, p]));
  const ppmStats = PPM_SERVICES.map(s => {
    const row = ppmByService.get(s.key);
    return { ...s, row, status: ppmStatus(row) };
  });

  const counts = ppmStats.reduce((acc, s) => {
    acc[s.status.label] = (acc[s.status.label] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const tabs: { key: TabKey; label: string; icon: typeof LayoutDashboard }[] = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    ...(isCompany ? [] : [{ key: "requests" as TabKey, label: "My Requests", icon: FileText }]),
    { key: "ppm", label: "PPM Schedules", icon: ClipboardList },
    { key: "callouts", label: "Call Outs", icon: AlertTriangle },
    { key: "account", label: "Account", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 max-w-6xl py-4 flex items-center justify-between">
          <Link href="/">
            <img src={logo} alt="VertexAir" className="h-10 cursor-pointer" />
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:inline">
              {isCompany ? <Building2 className="inline w-4 h-4 mr-1" /> : <User className="inline w-4 h-4 mr-1" />}
              <span className="font-semibold text-foreground">{user.name}</span>
            </span>
            <button onClick={handleLogout} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 hover:text-red-600 transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 max-w-6xl py-8">
        <div className="bg-gradient-to-br from-primary to-blue-700 rounded-2xl text-white p-6 md:p-8 mb-6 shadow-lg">
          <div className="text-xs uppercase tracking-wider text-blue-200 font-semibold mb-1">{isCompany ? "Commercial portal" : "Customer portal"}</div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Welcome back, {firstName}!</h1>
          <p className="text-blue-100 mb-5 text-sm md:text-base">
            {isCompany
              ? "View your maintenance schedules, track upcoming services and manage your account."
              : "Manage your quotes, service requests and PPM maintenance in one place."}
          </p>
          <div className="flex flex-wrap gap-3">
            {!isCompany && (
              <Link href="/quote">
                <button className="flex items-center gap-2 px-5 py-2.5 bg-white text-primary font-semibold rounded-lg hover:bg-blue-50 transition-all shadow-md">
                  <Plus className="w-4 h-4" /> Request New Quote
                </button>
              </Link>
            )}
            <Link href="/contact">
              <button className="flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all">
                <Mail className="w-4 h-4" /> Contact Us
              </button>
            </Link>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6 overflow-x-auto">
          <div className="flex">
            {tabs.map(t => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                    active ? "border-primary text-primary bg-primary/5" : "border-transparent text-slate-600 hover:text-foreground hover:bg-slate-50"
                  }`}
                >
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {tab === "overview" && (
          <OverviewTab user={user} ppmStats={ppmStats} counts={counts} subs={subs} onGo={(t) => setTab(t)} isCompany={isCompany} />
        )}
        {tab === "requests" && !isCompany && <RequestsTab subs={subs} />}
        {tab === "ppm" && <PpmTab ppmStats={ppmStats} counts={counts} />}
        {tab === "callouts" && <CallOutsTab />}
        {tab === "account" && <AccountTab user={user} onUpdated={(u) => setUser(u)} />}
      </main>
    </div>
  );
}

function OverviewTab({ user, ppmStats, counts, subs, onGo, isCompany }: {
  user: PortalUser; ppmStats: ReturnType<typeof Array.prototype.map>; counts: Record<string, number>; subs: Submission[]; onGo: (t: TabKey) => void; isCompany: boolean;
}) {
  const overdue = counts["Overdue"] || 0;
  const due = counts["Due"] || 0;
  const ok = counts["OK"] || 0;
  const missing = counts["Missing"] || 0;

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            {isCompany ? <Building2 className="w-5 h-5 text-primary" /> : <User className="w-5 h-5 text-primary" />}
          </div>
          <h2 className="font-bold text-foreground">My Profile</h2>
        </div>
        <div className="space-y-3 text-sm">
          <Field label={isCompany ? "Trading name" : "Name"} value={user.name} />
          {isCompany && user.operatingCompany && <Field label="Operating company" value={user.operatingCompany} />}
          <Field label="Email" value={user.email} mono />
          {user.phone && <Field label="Phone" value={user.phone} />}
          {user.address && <Field label="Address" value={user.address} />}
          <Field label="Member since" value={new Date(user.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })} />
        </div>
      </div>

      <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
        <button onClick={() => onGo("ppm")} className="text-left bg-white rounded-2xl shadow-sm border border-slate-200 p-5 hover:border-primary/40 hover:shadow-md transition-all group">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center"><AlertCircle className="w-5 h-5" /></div>
            <span className="text-3xl font-extrabold text-red-600">{overdue}</span>
          </div>
          <div className="font-bold text-foreground">Overdue PPMs</div>
          <div className="text-xs text-muted-foreground mt-0.5">Need attention now</div>
        </button>
        <button onClick={() => onGo("ppm")} className="text-left bg-white rounded-2xl shadow-sm border border-slate-200 p-5 hover:border-primary/40 hover:shadow-md transition-all group">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center"><Clock className="w-5 h-5" /></div>
            <span className="text-3xl font-extrabold text-amber-600">{due}</span>
          </div>
          <div className="font-bold text-foreground">Due Soon</div>
          <div className="text-xs text-muted-foreground mt-0.5">Within the next 30 days</div>
        </button>
        <button onClick={() => onGo("ppm")} className="text-left bg-white rounded-2xl shadow-sm border border-slate-200 p-5 hover:border-primary/40 hover:shadow-md transition-all group">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center"><CheckCircle2 className="w-5 h-5" /></div>
            <span className="text-3xl font-extrabold text-emerald-600">{ok}</span>
          </div>
          <div className="font-bold text-foreground">In Date</div>
          <div className="text-xs text-muted-foreground mt-0.5">Currently compliant</div>
        </button>
        <button onClick={() => onGo("ppm")} className="text-left bg-white rounded-2xl shadow-sm border border-slate-200 p-5 hover:border-primary/40 hover:shadow-md transition-all group">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center"><ClipboardList className="w-5 h-5" /></div>
            <span className="text-3xl font-extrabold text-slate-600">{missing}</span>
          </div>
          <div className="font-bold text-foreground">No Schedule</div>
          <div className="text-xs text-muted-foreground mt-0.5">Not set up yet</div>
        </button>

        {!isCompany && (
          <div className="sm:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />
                <span className="font-bold text-foreground">Recent Requests</span>
              </div>
              <button onClick={() => onGo("requests")} className="text-xs font-semibold text-primary hover:underline">View all →</button>
            </div>
            {subs.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No requests submitted yet.</p>
            ) : (
              <div className="space-y-2">
                {subs.slice(0, 3).map(s => (
                  <div key={s.id} className="flex items-center justify-between gap-2 text-sm p-2 rounded-lg hover:bg-slate-50">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[10px] uppercase tracking-wider font-semibold bg-primary/10 text-primary px-2 py-0.5 rounded-full">{s.formType}</span>
                      <span className="truncate text-foreground">{s.subject || s.message?.slice(0, 50) || "—"}</span>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">{new Date(s.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function RequestsTab({ subs }: { subs: Submission[] }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center"><FileText className="w-5 h-5 text-primary" /></div>
          <h2 className="font-bold text-foreground">My Requests</h2>
        </div>
        <span className="text-sm text-muted-foreground">{subs.length} total</span>
      </div>

      {subs.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-muted-foreground mb-4">You haven't submitted any requests yet.</p>
          <Link href="/quote">
            <button className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-all">
              Get Your First Quote <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {subs.map((sub) => {
            const meta = STATUS_META[sub.status || "new"] || STATUS_META.new;
            const StatusIcon = meta.icon;
            return (
              <div key={sub.id} className="border border-slate-200 rounded-xl p-4 hover:border-primary/30 transition-colors">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium uppercase tracking-wider">{sub.formType}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${meta.color}`}>
                      <StatusIcon className="w-3 h-3" /> {meta.label}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0">
                    <Clock className="w-3 h-3" />
                    {new Date(sub.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                  </span>
                </div>
                {sub.subject && <p className="font-semibold text-foreground text-sm mb-1">{sub.subject}</p>}
                {sub.message && <p className="text-sm text-muted-foreground line-clamp-2">{sub.message}</p>}
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-muted-foreground">
                  {sub.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {sub.phone}</span>}
                  {sub.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {sub.email}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PpmTab({ ppmStats, counts }: { ppmStats: any[]; counts: Record<string, number> }) {
  const [reportsFor, setReportsFor] = useState<{ key: string; label: string } | null>(null);
  const [reportCounts, setReportCounts] = useState<Record<string, number>>({});

  const loadCounts = async () => {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/customer/ppm/reports/counts`, { credentials: "include" });
      if (res.ok) setReportCounts(await res.json());
    } catch {}
  };

  useEffect(() => { loadCounts(); }, []);
  const chartData = ppmStats.map(s => ({
    name: s.label,
    days: s.status.days,
    value: s.row?.dueDate ? 1 : 0,
  }));

  const pieData = [
    { name: "In Date", value: counts["OK"] || 0, color: "#10b981" },
    { name: "Due Soon", value: counts["Due"] || 0, color: "#f59e0b" },
    { name: "Overdue", value: counts["Overdue"] || 0, color: "#dc2626" },
    { name: "Missing", value: counts["Missing"] || 0, color: "#94a3b8" },
  ].filter(p => p.value > 0);

  const hasAny = ppmStats.some(s => s.row);

  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 lg:col-span-2">
          <h3 className="font-bold text-foreground mb-1">Maintenance Status</h3>
          <p className="text-xs text-muted-foreground mb-4">Days until next service due — negative bars are overdue</p>
          {!hasAny ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No PPM schedules have been set up for your account yet. Please contact us to arrange a maintenance plan.
            </div>
          ) : (
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={chartData.filter(d => d.days !== null)} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-15} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} label={{ value: "Days", angle: -90, position: "insideLeft", style: { fill: "#64748b", fontSize: 11 } }} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                    formatter={(v: any) => [`${v} days`, v < 0 ? "Overdue by" : "Due in"]}
                  />
                  <Bar dataKey="days" radius={[4, 4, 0, 0]}>
                    {chartData.filter(d => d.days !== null).map((entry, i) => (
                      <Cell key={i} fill={entry.days! < 0 ? "#dc2626" : entry.days! <= 30 ? "#f59e0b" : "#10b981"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <h3 className="font-bold text-foreground mb-1">Compliance Overview</h3>
          <p className="text-xs text-muted-foreground mb-4">Distribution across all services</p>
          {pieData.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">No data yet.</div>
          ) : (
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {pieData.map((p, i) => <Cell key={i} fill={p.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100">
          <h3 className="font-bold text-foreground">PPM Schedule Details</h3>
          <p className="text-xs text-muted-foreground">All scheduled maintenance services for your account</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Service</th>
                <th className="text-left px-5 py-3 font-semibold">Last Serviced</th>
                <th className="text-left px-5 py-3 font-semibold">Next Due</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-left px-5 py-3 font-semibold">Notes</th>
                <th className="text-right px-5 py-3 font-semibold w-32">Files</th>
              </tr>
            </thead>
            <tbody>
              {ppmStats.map(s => {
                const Icon = s.icon;
                return (
                  <tr key={s.key} className="border-t border-slate-100">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s.color}1a`, color: s.color }}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <span className="font-semibold text-foreground">{s.label}</span>
                        {reportCounts[s.key] > 0 && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold" title={`${reportCounts[s.key]} report${reportCounts[s.key] > 1 ? "s" : ""} on file`}>
                            <FileCheck2 className="w-3 h-3" />
                            {reportCounts[s.key]}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-foreground">
                      {s.row?.lastServiced ? new Date(s.row.lastServiced).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-5 py-3 text-foreground">
                      {s.row?.dueDate ? new Date(s.row.dueDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${s.status.bg} ${s.status.color}`}>
                        {s.status.label}
                        {s.status.days !== null && s.status.label !== "OK" && (
                          <span className="opacity-70">· {Math.abs(s.status.days)}d</span>
                        )}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-muted-foreground text-xs">{s.row?.notes || <span className="text-slate-300">—</span>}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => setReportsFor({ key: s.key, label: s.label })} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 transition-colors">
                        <FolderOpen className="w-3.5 h-3.5" /> Reports
                        {reportCounts[s.key] > 0 && (
                          <span className="ml-0.5 px-1.5 rounded-full bg-sky-600 text-white text-[10px] font-bold">{reportCounts[s.key]}</span>
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {reportsFor && (
        <PortalReportsModal serviceKey={reportsFor.key} serviceLabel={reportsFor.label} onClose={() => setReportsFor(null)} />
      )}
    </div>
  );
}

type PortalReportRow = {
  id: number;
  fileName: string;
  mimeType: string;
  fileSize: number;
  reportDate: string | null;
  notes: string | null;
  uploadedAt: string;
};

function PortalReportsModal({ serviceKey, serviceLabel, onClose }: { serviceKey: string; serviceLabel: string; onClose: () => void }) {
  const [rows, setRows] = useState<PortalReportRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${import.meta.env.BASE_URL}api/customer/ppm/reports?serviceType=${serviceKey}`, { credentials: "include" });
        if (res.ok && !cancelled) setRows(await res.json());
      } catch {} finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [serviceKey]);

  const fmtSize = (n: number) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(2)} MB`;
  const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center">
            <FolderOpen className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-foreground">{serviceLabel} — Reports & Certificates</h2>
            <p className="text-xs text-muted-foreground">View and download service documents for your account</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-muted-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16 px-6 text-muted-foreground text-sm">
              <FolderOpen className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              No reports have been uploaded for {serviceLabel} yet.
              <div className="text-xs mt-2">Once your engineer completes a visit, the certificate will appear here.</div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100 sticky top-0">
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-5 py-3 font-semibold">Date</th>
                  <th className="px-5 py-3 font-semibold">File</th>
                  <th className="px-5 py-3 font-semibold">Notes</th>
                  <th className="px-5 py-3 font-semibold w-32 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                    <td className="px-5 py-3 font-medium text-foreground">{fmtDate(r.reportDate)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-sky-600 shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium text-foreground truncate max-w-[16rem]" title={r.fileName}>{r.fileName}</div>
                          <div className="text-xs text-muted-foreground">{fmtSize(r.fileSize)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 max-w-[14rem]">
                      {r.notes ? <span className="text-foreground/70 line-clamp-1" title={r.notes}>{r.notes}</span> : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <a href={`${import.meta.env.BASE_URL}api/customer/ppm/reports/${r.id}/download`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200">
                        <Download className="w-3.5 h-3.5" /> Download
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

type CalloutRow = {
  id: number;
  callDate: string;
  callType: string;
  priority: "low" | "medium" | "high" | "emergency";
  status: "open" | "in_progress" | "resolved";
  engineer: string | null;
  description: string | null;
  resolvedDate: string | null;
  siteAddress: string | null;
  parkingAvailable: "yes" | "no" | "unknown" | null;
  siteContactName: string | null;
  siteContactPhone: string | null;
};

const CO_PRIORITY: Record<string, { label: string; cls: string }> = {
  low: { label: "Low", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  medium: { label: "Medium", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  high: { label: "High", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  emergency: { label: "Emergency", cls: "bg-red-50 text-red-700 border-red-200" },
};
const CO_STATUS: Record<string, { label: string; cls: string }> = {
  open: { label: "Open", cls: "bg-red-50 text-red-700 border-red-200" },
  in_progress: { label: "In Progress", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  resolved: { label: "Resolved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

function CallOutsTab() {
  const [rows, setRows] = useState<CalloutRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<number | null>(null);
  const [attachCounts, setAttachCounts] = useState<Record<number, number>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${import.meta.env.BASE_URL}api/customer/callouts`, { credentials: "include" });
        if (res.ok && !cancelled) {
          const data: CalloutRow[] = await res.json();
          setRows(data);
          const counts = await Promise.all(data.map(async d => {
            const r = await fetch(`${import.meta.env.BASE_URL}api/customer/callouts/${d.id}/attachments`, { credentials: "include" });
            return [d.id, r.ok ? (await r.json()).length : 0] as [number, number];
          }));
          if (!cancelled) setAttachCounts(Object.fromEntries(counts));
        }
      } catch {} finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const summary = {
    open: rows.filter(r => r.status === "open").length,
    inProgress: rows.filter(r => r.status === "in_progress").length,
    resolved: rows.filter(r => r.status === "resolved").length,
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total Call Outs</div>
          <div className="text-3xl font-bold text-foreground">{rows.length}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Open</div>
          <div className="text-3xl font-bold text-red-600">{summary.open}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">In Progress</div>
          <div className="text-3xl font-bold text-amber-600">{summary.inProgress}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Resolved</div>
          <div className="text-3xl font-bold text-emerald-600">{summary.resolved}</div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100">
          <h3 className="font-bold text-foreground">Call Outs Schedule</h3>
          <p className="text-xs text-muted-foreground">Reactive visits & emergency call-outs logged on your account</p>
        </div>
        {loading ? (
          <div className="text-center py-12 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 px-6 text-muted-foreground text-sm">
            <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            No call-outs have been logged on your account.
            <div className="text-xs mt-2">If you need an emergency repair, please <Link href="/contact"><span className="text-primary underline cursor-pointer">contact us</span></Link>.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Date</th>
                  <th className="text-left px-5 py-3 font-semibold">Type</th>
                  <th className="text-left px-5 py-3 font-semibold">Priority</th>
                  <th className="text-left px-5 py-3 font-semibold">Status</th>
                  <th className="text-left px-5 py-3 font-semibold">Engineer</th>
                  <th className="text-left px-5 py-3 font-semibold">Resolved</th>
                  <th className="text-right px-5 py-3 font-semibold w-32">Files</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-5 py-3 text-foreground whitespace-nowrap">{fmtDate(r.callDate)}</td>
                    <td className="px-5 py-3 text-foreground font-medium">
                      {r.callType}
                      {r.description && <div className="text-xs text-muted-foreground font-normal mt-0.5 line-clamp-1">{r.description}</div>}
                      {r.siteAddress && (
                        <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(r.siteAddress)}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-xs text-primary hover:underline font-normal mt-0.5 line-clamp-1 block" title="Open directions in Google Maps">📍 {r.siteAddress}</a>
                      )}
                      {(r.siteContactName || r.siteContactPhone) && (
                        <div className="text-xs text-muted-foreground font-normal mt-0.5">
                          Site contact: {r.siteContactName || "—"}{r.siteContactPhone && <> · <a href={`tel:${r.siteContactPhone}`} className="text-primary hover:underline">{r.siteContactPhone}</a></>}
                        </div>
                      )}
                      {r.parkingAvailable && r.parkingAvailable !== "unknown" && (
                        <div className="text-xs text-muted-foreground font-normal mt-0.5">Parking: {r.parkingAvailable === "yes" ? "Available on site" : "Not available"}</div>
                      )}
                    </td>
                    <td className="px-5 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${CO_PRIORITY[r.priority].cls}`}>{CO_PRIORITY[r.priority].label}</span></td>
                    <td className="px-5 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${CO_STATUS[r.status].cls}`}>{CO_STATUS[r.status].label}</span></td>
                    <td className="px-5 py-3 text-foreground">{r.engineer || "—"}</td>
                    <td className="px-5 py-3 text-muted-foreground">{fmtDate(r.resolvedDate)}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => setOpenId(r.id)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 transition-colors">
                        <Paperclip className="w-3.5 h-3.5" /> Files
                        {attachCounts[r.id] > 0 && <span className="ml-0.5 px-1.5 rounded-full bg-sky-600 text-white text-[10px] font-bold">{attachCounts[r.id]}</span>}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openId !== null && (
        <CalloutAttachmentsModal callOutId={openId} onClose={() => setOpenId(null)} />
      )}
    </div>
  );
}

function CalloutAttachmentsModal({ callOutId, onClose }: { callOutId: number; onClose: () => void }) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${import.meta.env.BASE_URL}api/customer/callouts/${callOutId}/attachments`, { credentials: "include" });
        if (r.ok && !cancelled) setItems(await r.json());
      } catch {} finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [callOutId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center"><FolderOpen className="w-5 h-5" /></div>
          <div className="flex-1">
            <h2 className="font-bold text-foreground">Call Out Attachments</h2>
            <p className="text-xs text-muted-foreground">Reports, photos and documents from this call-out</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-muted-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">No attachments for this call-out yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map(it => (
                <li key={it.id} className="py-3 flex items-center gap-3">
                  <FolderOpen className="w-5 h-5 text-sky-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{it.fileName}</div>
                    <div className="text-xs text-muted-foreground">{(it.fileSize / 1024).toFixed(1)} KB · {new Date(it.uploadedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}{it.notes ? ` · ${it.notes}` : ""}</div>
                  </div>
                  <a href={`${import.meta.env.BASE_URL}api/customer/callouts/attachments/${it.id}/download`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-white hover:bg-primary/90 transition-colors"><Download className="w-3.5 h-3.5" /> Download</a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function AccountTab({ user, onUpdated }: { user: PortalUser; onUpdated: (u: PortalUser) => void }) {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    if (newPw.length < 6) { setMsg({ type: "err", text: "New password must be at least 6 characters" }); return; }
    if (newPw !== confirmPw) { setMsg({ type: "err", text: "New passwords do not match" }); return; }
    setSaving(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/customer/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ currentPassword: currentPw, newPassword: newPw }),
      });
      if (res.ok) {
        setMsg({ type: "ok", text: "Password updated successfully." });
        setCurrentPw(""); setNewPw(""); setConfirmPw("");
      } else {
        const d = await res.json().catch(() => ({}));
        setMsg({ type: "err", text: d.error || "Failed to update password" });
      }
    } catch { setMsg({ type: "err", text: "Network error" }); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            {user.kind === "company" ? <Building2 className="w-5 h-5 text-primary" /> : <User className="w-5 h-5 text-primary" />}
          </div>
          <h2 className="font-bold text-foreground">Account Details</h2>
        </div>
        <div className="space-y-3 text-sm">
          <Field label={user.kind === "company" ? "Trading name" : "Name"} value={user.name} />
          {user.kind === "company" && user.operatingCompany && <Field label="Operating company" value={user.operatingCompany} />}
          <Field label="Email" value={user.email} mono />
          {user.phone && <Field label="Phone" value={user.phone} />}
          {user.address && <Field label="Address" value={user.address} />}
          {user.kind === "company" && user.contactName && <Field label="Primary contact" value={user.contactName} />}
          {user.kind === "company" && user.website && <Field label="Website" value={user.website} mono />}
          <Field label="Member since" value={new Date(user.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })} />
          <p className="text-xs text-muted-foreground pt-2 border-t border-slate-100">
            To update profile details, please contact us — these are managed by VertexAir staff to keep your records accurate.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center"><Lock className="w-5 h-5" /></div>
          <h2 className="font-bold text-foreground">Change Password</h2>
        </div>

        <div className="space-y-3">
          <PwField label="Current password" value={currentPw} onChange={setCurrentPw} show={showCurrent} onToggle={() => setShowCurrent(s => !s)} />
          <PwField label="New password (min. 6 characters)" value={newPw} onChange={setNewPw} show={showNew} onToggle={() => setShowNew(s => !s)} />
          <PwField label="Confirm new password" value={confirmPw} onChange={setConfirmPw} show={showNew} onToggle={() => setShowNew(s => !s)} />
        </div>

        {msg && (
          <div className={`mt-4 text-sm px-3 py-2 rounded-lg border ${msg.type === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`}>
            {msg.text}
          </div>
        )}

        <button
          type="submit"
          disabled={saving || !currentPw || !newPw || !confirmPw}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-white font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Update password
        </button>
      </form>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className={`font-medium text-foreground break-words ${mono ? "font-mono text-xs" : ""}`}>{value}</p>
    </div>
  );
}

function PwField({ label, value, onChange, show, onToggle }: { label: string; value: string; onChange: (v: string) => void; show: boolean; onToggle: () => void }) {
  return (
    <div>
      <label className="text-xs font-semibold text-foreground block mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full pl-3 pr-10 py-2.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />
        <button type="button" onClick={onToggle} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-foreground">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
